import { Divider, useTheme } from '@mui/material';
import {
  CustomInput,
  CustomSelect,
  Heading4,
  StepContent,
} from 'app/components';
import {
  ACCOUNT_TYPE_ARRAY,
  ACCOUNT_TYPE_OBJECT,
  BANK_LIST_ARRAY,
  BANK_LIST_OBJECT,
} from 'app/helpers';
import { useFormik } from 'formik';
import PixSelector, {
  applyFormat,
  getErrorValidation,
  getPixTypeFromPixTypeToggle,
  getPlaceholder,
  PixTypeToggle,
} from 'modules/customer/components/PixSelector/PixSelector';
import { PixType } from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { bankStepSchema } from 'modules/customer/helpers/personValidation';
import { useEffect, useState } from 'react';
import {
  StepContentInput,
  StepContentInputArea,
} from '../About/CreatePersonForm/styles';
import { IBankInfo } from './types';
import { StyledCustomAccount, StyledTitle } from './styles';

interface IBankStepProps {
  setBankInfo: (bankInfo: IBankInfo) => void;
  bankInfo?: IBankInfo;
  hideSection?: boolean;
}
const BankStep = ({ bankInfo, setBankInfo, hideSection }: IBankStepProps) => {
  const theme = useTheme();
  const [pixType, togglePixType] = useState<PixTypeToggle>(
    bankInfo?.pix?.key_type
      ? (bankInfo?.pix?.key_type as unknown as PixTypeToggle)
      : PixTypeToggle.CPF,
  );
  const [pixKey, updatePixKey] = useState<string>(bankInfo?.pix?.key || '');
  const formik = useFormik({
    initialValues: {
      key: pixKey,
      ispb_code: bankInfo?.external_bank_account?.ispb_code
        ? {
            value: bankInfo?.external_bank_account?.ispb_code,
            label:
              BANK_LIST_OBJECT[
                bankInfo?.external_bank_account?.ispb_code as string
              ],
          }
        : undefined,
      bank_account: bankInfo?.external_bank_account?.bank_account,
      bank_account_digit: bankInfo?.external_bank_account?.bank_account_digit,
      bank_branch: bankInfo?.external_bank_account?.bank_branch,
      bank_account_type: bankInfo?.external_bank_account?.bank_account_type
        ? {
            value: bankInfo?.external_bank_account?.bank_account_type,
            label:
              ACCOUNT_TYPE_OBJECT[
                bankInfo?.external_bank_account?.bank_account_type as string
              ],
          }
        : undefined,
    },
    enableReinitialize: true,
    validationSchema: bankStepSchema,
    onSubmit: async (values) => console.info(values),
  });

  const pixValidation =
    formik.values.key !== '' && getErrorValidation(pixType, formik.values.key);

  useEffect(() => {
    if (bankInfo?.pix?.key) {
      updatePixKey(bankInfo?.pix?.key);
      formik.setFieldValue('key', bankInfo?.pix?.key);
    }
  }, [bankInfo]);

  const bankContent = (
    <StepContentInputArea>
      <StepContentInput>
        <CustomSelect
          labelValue="Banco"
          name="ispb_code"
          placeholder="Selecione o banco"
          value={formik.values.ispb_code}
          handleInputChange={formik.handleChange}
          onBlur={formik.handleBlur}
          options={BANK_LIST_ARRAY}
          validationError={
            formik.touched?.ispb_code ? formik.errors?.ispb_code : ''
          }
        />
      </StepContentInput>
      <StepContentInput isHalf={true}>
        <CustomInput
          name="bank_branch"
          value={formik.values.bank_branch || ''}
          handleInputChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="0000"
          labelValue="Agência"
          validationError={
            formik.touched.bank_branch ? formik.errors.bank_branch : ''
          }
        />
      </StepContentInput>
      <StepContentInput isHalf={true}>
        <StyledCustomAccount>
          <CustomInput
            name="bank_account"
            value={formik.values.bank_account || ''}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="000000"
            labelValue="Conta"
            validationError={
              formik.touched.bank_account ? formik.errors.bank_account : ''
            }
            style={{
              marginRight: '1rem',
            }}
          />
          <p style={{ marginTop: '2rem' }}>-</p>
          <CustomInput
            name="bank_account_digit"
            value={formik.values.bank_account_digit || ''}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="0"
            labelValue="Dígito"
            validationError={
              formik.touched.bank_account_digit
                ? formik.errors.bank_account_digit
                : ''
            }
          />
        </StyledCustomAccount>
      </StepContentInput>
      <StepContentInput>
        <CustomSelect
          labelValue="Tipo de conta"
          name="bank_account_type"
          placeholder="Selecione o tipo"
          value={formik.values.bank_account_type}
          handleInputChange={formik.handleChange}
          onBlur={formik.handleBlur}
          options={ACCOUNT_TYPE_ARRAY}
          validationError={
            formik.touched?.bank_account_type
              ? formik.errors?.bank_account_type
              : ''
          }
        />
      </StepContentInput>
    </StepContentInputArea>
  );
  const pixContent = (
    <>
      <PixSelector
        pix={bankInfo?.pix}
        onChange={(pixKeySelector, pixTypeSelector) => {
          formik.setFieldValue('key', '');
          updatePixKey(pixKeySelector);
          togglePixType(pixTypeSelector);
        }}
      />
      <StepContentInputArea>
        <StepContentInput>
          <CustomInput
            name="key"
            value={applyFormat(formik.values.key, pixType)}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={getPlaceholder(pixType)}
            labelValue="Insira abaixo a chave Pix"
            validationError={
              formik.touched.key || formik.submitCount > 0
                ? (formik.errors.key as string) || pixValidation || ''
                : ''
            }
          />
        </StepContentInput>
      </StepContentInputArea>
    </>
  );

  useEffect(() => {
    const keyType = getPixTypeFromPixTypeToggle(pixType);
    const key = formik.values.key.replace('+55', '');

    const parsedKey =
      keyType === PixType.PHONE_NUMBER
        ? `+55${key.replace(/[^\w\s]/gi, '').replace(/\s/g, '')}`
        : keyType === PixType.TAXPAYER_ID
        ? formik.values.key.replace(/[^\w\s]/gi, '')
        : keyType === PixType.EMAIL
        ? formik.values.key.toLowerCase()
        : formik.values.key.trim();

    setBankInfo({
      external_bank_account: {
        ispb_code: formik.values.ispb_code?.value as string,
        bank_account: formik.values.bank_account,
        bank_account_digit: formik.values.bank_account_digit,
        bank_branch: formik.values.bank_branch,
        bank_account_type: formik.values.bank_account_type?.value as string,
      },
      pix: {
        key: parsedKey,
        key_type: keyType,
      },
    });
  }, [formik.values]);

  return hideSection ? (
    <>
      <StyledTitle>Dados bancários</StyledTitle>
      {bankContent}
      <Divider style={{ margin: '1rem 0' }} />
      <StyledTitle>Chave Pix</StyledTitle>
      <br />
      {pixContent}
    </>
  ) : (
    <StepContent title="Dados bancários" description="">
      {bankContent}

      <Heading4
        fontWeight="B"
        style={{
          color: theme.palette.brand.primary.base,
          marginBottom: '32px',
          marginTop: '32px',
        }}
      >
        Pix
      </Heading4>
      {pixContent}
    </StepContent>
  );
};

export default BankStep;
